// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import {
    getProductLineProductNumber,
    resolveProductLineMinimumOrderQuantity,
    resolveProductMinimumOrderQuantity,
} from "@store/Framework/Common/Utilities/productLineUtilities";
import resolveEntryForPlant from "@store/Framework/Common/Utilities/resolveEntryForPlant";
import {
    FullTruckLoadEntryModel,
    FullTruckLoadModel,
    LeadTimeEntryModel,
    LeadTimeModel,
} from "@store/Framework/Types/ApiModels";
import ProductFullTruckLoad from "@store/Library/Components/ProductFullTruckLoad";
import ProductLeadTime from "@store/Library/Components/ProductLeadTime";
import { ProductLineComponentModel } from "@store/Library/Components/ProductLine/ProductLine";
import ProductLineMinimumOrderQuantity from "@store/Library/Components/ProductLine/ProductLineMinimumOrderQuantity";
import ProductShipsFrom from "@store/Library/Components/ProductShipsFrom";
import getBooleanProperty from "@toolkit/Framework/Common/Utilities/getBooleanProperty";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { spacingStyles } from "@toolkit/Themes/common/mixins";
import React, { FC, useContext } from "react";
import { css } from "styled-components";

export interface ProductLineFulfillmentProps {
    productLine?: ProductLineComponentModel;
}

export interface ProductLineFulfillmentStyles {
    wrapper?: InjectableCss;
}

export const productLineFulfillmentStyles: ProductLineFulfillmentStyles = {
    wrapper: {
        css: css`
            ${spacingStyles(null, pxToRem(6))}
            display: flex;
            flex-wrap: wrap;
            gap: ${pxToRem(8, 24)};
        `,
    },
};

const styles = productLineFulfillmentStyles;

const ProductLineFulfillment: FC<ProductLineFulfillmentProps> = ({ productLine }) => {
    const { product, productInfo } = useContext(ProductContext);
    const hasProductContext = product && productInfo;

    let pricing: ProductLineComponentModel["pricing"];
    let overrideFtl: FullTruckLoadModel | undefined;
    let overrideLT: LeadTimeModel | undefined;
    let warehouseDescription: string | undefined;
    // Resolved independently of the lead time - a product can have a minimum order quantity
    // without a maintained lead time, and vice versa (CDP-4430).
    let minimumOrderQuantity = resolveProductMinimumOrderQuantity(product, productInfo);

    if (!hasProductContext) {
        if (!productLine) {
            return null;
        }

        pricing = productLine.pricing;
        const productNumber = getProductLineProductNumber(productLine);
        const plantFromPricing = pricing?.additionalResults?.warehouseName;

        // Existing order lines have no real-time pricing, so their warehouse description comes
        // from a static property (backend-resolved from the order line's warehouse code) instead.
        warehouseDescription =
            pricing?.additionalResults?.warehouseDescription || productLine.properties.warehouseDescription;

        const leadTimeEntryModel = resolveEntryForPlant<LeadTimeEntryModel>(
            productLine.properties.leadTimes,
            productNumber,
            plantFromPricing,
        );

        const fullTruckLoadEntryModel = resolveEntryForPlant<FullTruckLoadEntryModel>(
            productLine.properties.fullTruckLoads,
            productNumber,
            plantFromPricing,
        );

        const ftlQuantity =
            pricing?.additionalResults?.fullTruckLoad ||
            fullTruckLoadEntryModel?.FullTruckLoad ||
            productLine.properties.fullTruckLoad;

        const plant =
            plantFromPricing ||
            leadTimeEntryModel?.Plant ||
            fullTruckLoadEntryModel?.Plant ||
            productLine.properties.warehouseName;

        const leadTimeDays =
            Number(
                pricing?.additionalResults?.leadTime || leadTimeEntryModel?.LeadTime || productLine.properties.leadTime,
            ) || undefined;

        const isSpecialItem = pricing?.additionalResults?.isSpecialItem
            ? getBooleanProperty(pricing.additionalResults.isSpecialItem)
            : (leadTimeEntryModel?.IsSpecialItem ?? getBooleanProperty(productLine.properties.isSpecialItem));

        minimumOrderQuantity = resolveProductLineMinimumOrderQuantity(productLine);

        if (ftlQuantity) {
            overrideFtl = { ftlQuantity, plant };
        }

        overrideLT = {
            isSpecialItem,
            leadTimeDays,
            minimumOrderQuantity,
            plant,
        };
    }

    return (
        <StyledWrapper {...styles.wrapper}>
            {(hasProductContext || pricing || warehouseDescription) && (
                <ProductShipsFrom pricing={pricing} overrideWarehouseDescription={warehouseDescription} displayIcons />
            )}
            {(hasProductContext || overrideFtl) && <ProductFullTruckLoad overrideFtl={overrideFtl} displayIcons />}
            {(hasProductContext || overrideLT) && (
                <ProductLeadTime
                    displayAsStatus={false}
                    overrideLT={overrideLT}
                    showShortLeadTimeDesc
                    referenceProductId={!hasProductContext ? (productLine?.productId ?? productLine?.id) : undefined}
                />
            )}
            <ProductLineMinimumOrderQuantity minimumOrderQuantity={minimumOrderQuantity} />
        </StyledWrapper>
    );
};

export default ProductLineFulfillment;
